import type { PermissionAction, ScreenId } from "@agentcall/core/type/permission_type"
import type { ReactElement, ReactNode } from "react"
import { cloneElement, isValidElement } from "react"
import { usePermission } from "../hooks/usePermission"

interface PermissionGateProps {
  /** 権限チェック対象の画面ID */
  screenId: ScreenId
  /** チェックするアクション（デフォルト: read） */
  action?: PermissionAction
  /** 権限がある場合に表示する子要素 */
  children: ReactNode
  /** 権限がない場合に表示する要素（オプション） */
  fallback?: ReactNode
}

/**
 * 権限に基づいて子要素の表示/非表示を制御するコンポーネント
 *
 * @example
 * ```tsx
 * // 基本的な使い方（権限がない場合は非表示）
 * <PermissionGate screenId="properties" action="create">
 *   <Button>新規作成</Button>
 * </PermissionGate>
 *
 * // フォールバックを指定
 * <PermissionGate screenId="properties" action="delete" fallback={<span>削除権限がありません</span>}>
 *   <Button variant="destructive">削除</Button>
 * </PermissionGate>
 * ```
 */
export function PermissionGate({
  screenId,
  action = "read",
  children,
  fallback = null,
}: PermissionGateProps) {
  const { can } = usePermission()

  if (!can(screenId, action)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

interface PermissionButtonProps {
  /** 権限チェック対象の画面ID */
  screenId: ScreenId
  /** チェックするアクション */
  action: PermissionAction
  /** ボタン要素 */
  children: ReactElement
  /** 権限がない場合の動作モード */
  disabledMode?: "hide" | "disable"
}

/**
 * ボタン用の権限制御ラッパーコンポーネント
 *
 * @example
 * ```tsx
 * // 権限がない場合は非表示
 * <PermissionButton screenId="properties" action="create">
 *   <Button onClick={handleCreate}>新規作成</Button>
 * </PermissionButton>
 *
 * // 権限がない場合は無効化
 * <PermissionButton screenId="properties" action="delete" disabledMode="disable">
 *   <Button onClick={handleDelete}>削除</Button>
 * </PermissionButton>
 * ```
 */
export function PermissionButton({
  screenId,
  action,
  children,
  disabledMode = "hide",
}: PermissionButtonProps) {
  const { can } = usePermission()
  const hasPermission = can(screenId, action)

  if (!hasPermission && disabledMode === "hide") {
    return null
  }

  if (!hasPermission && disabledMode === "disable") {
    // 子要素をクローンしてdisabled属性を追加
    if (isValidElement(children)) {
      return cloneElement(children, {
        disabled: true,
        title: "この操作を行う権限がありません",
      } as Record<string, unknown>)
    }
    return null
  }

  return <>{children}</>
}

interface ConditionalRenderProps {
  /** 権限チェック対象の画面ID */
  screenId: ScreenId
  /** チェックするアクション（オプション、デフォルトはread） */
  action?: PermissionAction
  /** 権限がある場合に表示する子要素 */
  children: ReactNode
}

/**
 * 条件付きレンダリング用コンポーネント群を提供するフック
 *
 * @example
 * ```tsx
 * const { IfCan, IfCanAccess } = useConditionalRender()
 *
 * return (
 *   <>
 *     <IfCanAccess screenId="properties">
 *       <PropertyList />
 *     </IfCanAccess>
 *
 *     <IfCan screenId="properties" action="create">
 *       <CreateButton />
 *     </IfCan>
 *   </>
 * )
 * ```
 */
export function useConditionalRender() {
  const { can, canAccess } = usePermission()

  return {
    /**
     * 特定の画面・アクションの権限がある場合のみ子要素を表示
     */
    IfCan: ({ screenId, action = "read", children }: ConditionalRenderProps) =>
      can(screenId, action) ? children : null,

    /**
     * 画面アクセス権限がある場合のみ子要素を表示
     */
    IfCanAccess: ({ screenId, children }: Omit<ConditionalRenderProps, "action">) =>
      canAccess(screenId) ? children : null,
  }
}
